import { fetch } from 'http-common';

import { PostData, ShopType } from 'types';
import { ListType } from 'types/api/api.type';
import { ShopData } from 'types/api/customer.type';

type ShopDataList = Omit<ListType, 'content'> & { content: ShopData[] };

/**
 * `{id, customerId, customerShopId, status, timestamp, userId, customerShop:
 * {...fields}}` — same envelope shape as the Customer endpoints, flattened
 * the same way right after the HTTP call so form consumers keep seeing a
 * flat `ShopData`-shaped object.
 */
type ShopRecordEnvelope = {
  customerShopId: string;
  status: string;
  customerShop: Record<string, unknown>;
};

const flattenShop = (envelope: ShopRecordEnvelope): ShopData =>
  ({
    ...envelope.customerShop,
    id: envelope.customerShopId,
    status: envelope.status,
  } as ShopData);

class ShopsDataService {
  private http = fetch();

  // eslint-disable-next-line
  private ep = (id: string) => `/v1/customer-mapper/customers/${id}/shops`;

  public async getAll(id: string) {
    const response = await this.http.get<Omit<ListType, 'content'> & { content: unknown[] }>(
      this.ep(id)
    );

    return {
      ...response,
      data: {
        ...response.data,
        content: (response.data.content as ShopRecordEnvelope[]).map(flattenShop),
      },
    } as { data: ShopDataList };
  }

  public async get(id: string, shopId: string) {
    const response = await this.http.get<ShopRecordEnvelope>(`${this.ep(id)}/${shopId}`);

    return { ...response, data: flattenShop(response.data) };
  }

  /** Soft delete (any status except IN_REVIEW). For a non-admin deleting an
   * ACCEPTED shop, the backend appends DELETE_REQUESTED instead of actually
   * deleting — the caller (TableActions.tsx) checks the returned status to
   * tell which happened (IOOAEC-444). Was returning the raw, unflattened
   * envelope (no top-level `id`, unlike every other method here) — that's
   * what produced a phantom "UNDEFINED" row when TableActions.tsx dispatched
   * setShop(res.data) with a undefined id (confirmed live). */
  public async deleteShop(id: string, shopId: string) {
    const response = await this.http.delete<ShopRecordEnvelope>(`${this.ep(id)}/${shopId}`);
    return { ...response, data: flattenShop(response.data) };
  }

  public async post(id: string, data: PostData, type: ShopType) {
    const response = await this.http.post<ShopRecordEnvelope>(this.ep(id), {
      ...data,
      shopModel: type,
    });

    return { ...response, data: flattenShop(response.data) };
  }

  public async put(id: string, shopId: string, data: PostData, type: ShopType) {
    const response = await this.http.put<ShopRecordEnvelope>(`${this.ep(id)}/${shopId}`, {
      ...data,
      shopModel: type,
    });

    return { ...response, data: flattenShop(response.data) };
  }

  public async postSubmit(id: string, shopId: string) {
    const response = await this.http.post<ShopRecordEnvelope>(
      `${this.ep(id)}/${shopId}/submit`
    );

    return { ...response, data: flattenShop(response.data) };
  }

  public async accept(id: string, shopId: string) {
    const response = await this.http.post<ShopRecordEnvelope>(
      `${this.ep(id)}/${shopId}/accept`
    );

    return { ...response, data: flattenShop(response.data) };
  }

  public async refuse(id: string, shopId: string) {
    const response = await this.http.post<ShopRecordEnvelope>(
      `${this.ep(id)}/${shopId}/refuse`
    );

    return { ...response, data: flattenShop(response.data) };
  }
}

const shopsDataService = new ShopsDataService();

export { shopsDataService };
